import React, { useEffect } from "react";
import { motion } from "framer-motion";
import NavigationHeader from "../components/NavigationHeader";
import Footer from "../components/Footer";
import TopRatedProperties from "../components/TopRatedProperties";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Search, Home, Users, Shield, ArrowRight, ChevronDown, MoveRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useCurrentUser } from "../hooks/useCurrentUser";

const Index = () => {
  const { currentUser, loading } = useCurrentUser();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!loading && currentUser) {
      switch (currentUser.user_type) {
        case 'admin':
          navigate('/admin');
          break;
        case 'landlord':
          navigate('/owner-dashboard');
          break;
        case 'service_provider':
          navigate('/service-provider-dashboard');
          break;
        case 'tenant':
          break;
        default:
          break;
      }
    }
  }, [currentUser, loading, navigate]);

  if (loading) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-gradient-to-b from-blue-50 to-white font-['Tajawal',sans-serif] flex items-center justify-center"
      >
        <div className="text-center">
          <motion.div 
            animate={{ 
              rotate: 360,
              scale: [1, 1.2, 1]
            }}
            transition={{ 
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="w-12 h-12 bg-gradient-to-r from-blue-500  rounded-full mx-auto mb-6 flex items-center justify-center"
          >
            <Home className="h-6 w-6 text-white" />
          </motion.div>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-blue-800 text-lg"
          >
            جاري تحميل بياناتك...
          </motion.p>
        </div>
      </motion.div>
    );
  }

  const features = [
    {
      icon: Search,
      title: "البحث المتقدم",
      description: "ابحث عن العقار المثالي باستخدام فلاتر متقدمة"
    },
    {
      icon: Home,
      title: "عقارات متنوعة",
      description: "شقق، فيلل، ومكاتب في مختلف المناطق"
    },
    {
      icon: Users,
      title: "تواصل مباشر",
      description: "تواصل مع المالك مباشرة بدون وسطاء"
    },
    {
      icon: Shield,
      title: "أمان وثقة",
      description: "معاملات آمنة وموثقة لضمان راحتك"
    }
  ];

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 }
  };

  return (<>
          <NavigationHeader />

    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-gradient-to-b from-blue-50 to-white font-['Tajawal',sans-serif] flex flex-col overflow-x-hidden"
    >
      
      <div className="flex-1">
        {/* Hero Section */}
        <section className="container mx-auto px-4 py-12 sm:py-16 text-center relative">
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.1 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-gradient-to-br from-blue-200/20 to-cyan-300/20 blur-3xl -z-10"
          />
          
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.1 }}
            transition={{ duration: 1.5, ease: "easeOut", delay: 0.3 }}
            className="absolute bottom-10 left-0 w-80 h-80 rounded-full bg-gradient-to-tl from-cyan-200/20 to-blue-300/20 blur-3xl -z-10"
          />
          
          <div className="max-w-4xl mx-auto">
            <motion.div 
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ 
                type: "spring", 
                damping: 8, 
                stiffness: 100,
                delay: 0.1 
              }}
              className="mx-auto mb-8 w-24 h-24 rounded-full overflow-hidden shadow-xl bg-white border-4 border-white"
              whileHover={{ 
                scale: 1.1,
                boxShadow: "0 25px 50px -12px rgba(59, 130, 246, 0.3)"
              }}
            >
              <img 
                src="/lovable-uploads/kanflogo.png" 
                alt="شعار كنف" 
                className="w-full h-full object-cover"
              />
            </motion.div>

            <motion.h1 
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ type: "spring", damping: 10, delay: 0.2 }}
              className="text-4xl sm:text-5xl md:text-6xl font-bold text-blue-900 mb-6 leading-tight"
            >
              مرحباً بك في <span className="bg-gradient-to-r from-blue-700 to-cyan-600 bg-clip-text text-transparent">كنف</span>
            </motion.h1>
            
            <motion.p 
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, type: "spring" }}
              className="text-lg sm:text-xl text-blue-800 mb-8 leading-relaxed px-2"
            >
              منصتك الموثوقة للعثور على العقار المثالي أو تأجير عقارك بسهولة وأمان
            </motion.p>
            
            <motion.div 
              initial="hidden"
              animate="show"
              variants={container}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center px-4"
            >
              <motion.div variants={item} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  asChild 
                  size="lg" 
                  className="text-lg px-6 sm:px-8 py-3 w-full sm:w-auto bg-gradient-to-r from-blue-600  hover:from-blue-500 hover:to-cyan-600 shadow-lg hover:shadow-xl transition-all"
                >
                  <Link to="/properties">
                    <Search className="ml-2 h-5 w-5" />
                    ابحث عن عقار
                  </Link>
                </Button>
              </motion.div>
              
              <motion.div variants={item} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  asChild 
                  variant="outline" 
                  size="lg" 
                  className="text-lg px-6 sm:px-8 py-3 w-full sm:w-auto border-2 border-blue-600 text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-all"
                >
                  <Link to="/register">
                    أنشئ حساب جديد
                  </Link>
                </Button>
              </motion.div>
            </motion.div>
          </div>
          
          <motion.div 
            initial={{ y: -10 }}
            animate={{ y: 10 }}
            transition={{ 
              repeat: Infinity, 
              repeatType: "reverse", 
              duration: 1.5 
            }}
            className="absolute bottom-4 left-1/2 transform -translate-x-1/2 mt-16"
          >
            <ChevronDown className="h-8 w-8 text-blue-600 animate-pulse" />
          </motion.div>
        </section>

        {/* Statistics Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
        </motion.div>

        {/* Top Rated Properties Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          <TopRatedProperties />
        </motion.div>

        {/* How Kanef Works Section */}
        <motion.section 
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="container mx-auto px-4 py-16 bg-gradient-to-b from-blue-50 to-white"
        >
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-blue-900 mb-4">كيف تعمل منصة كنف؟</h2>
            <p className="text-blue-800 text-lg">خطوات بسيطة للوصول إلى عقارك المثالي</p>
          </motion.div>
          
          <div className="max-w-6xl mx-auto">
            <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-px h-96 bg-gradient-to-b from-blue-300/20 via-blue-400/40 to-blue-300/20" />
            
            <motion.div 
              className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={container}
            >
              {/* Step 1 */}
              <motion.div 
                variants={item}
                className="flex items-center gap-6 lg:justify-end"
                whileHover={{ x: -10, scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <div className="text-right lg:order-1">
                  <div className="flex items-center gap-2 mb-2 lg:justify-end">
                    <h3 className="text-xl font-bold text-blue-900">الخطوة 1</h3>
                    <span className="text-blue-700 font-semibold">سجل حسابك</span>
                  </div>
                  <p className="text-blue-800">انشئ حسابك في دقائق قليلة</p>
                </div>
                <motion.div 
                  className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full flex items-center justify-center lg:order-2 shadow-lg"
                  whileHover={{ 
                    scale: 1.1,
                    boxShadow: "0 25px 50px -12px rgba(59, 130, 246, 0.3)"
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Users className="h-8 w-8 text-white" />
                </motion.div>
              </motion.div>

              {/* Step 2 */}
              <motion.div 
                variants={item}
                className="flex items-center gap-6 lg:justify-start"
                whileHover={{ x: 10, scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div 
                  className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center shadow-lg"
                  whileHover={{ 
                    scale: 1.1,
                    boxShadow: "0 25px 50px -12px rgba(6, 182, 212, 0.3)"
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Search className="h-8 w-8 text-white" />
                </motion.div>
                <div className="text-left">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-cyan-600 font-semibold">ابحث عن عقار</span>
                    <h3 className="text-xl font-bold text-blue-900">الخطوة 2</h3>
                  </div>
                  <p className="text-blue-800">استخدم فلاتر البحث المتقدمة</p>
                </div>
              </motion.div>

              {/* Step 3 */}
              <motion.div 
                variants={item}
                className="flex items-center gap-6 lg:justify-end"
                whileHover={{ x: -10, scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <div className="text-right lg:order-1">
                  <div className="flex items-center gap-2 mb-2 lg:justify-end">
                    <h3 className="text-xl font-bold text-blue-900">الخطوة 3</h3>
                    <span className="text-blue-600 font-semibold">تواصل مع المالك</span>
                  </div>
                  <p className="text-blue-800">تواصل مباشرة مع مالك العقار</p>
                </div>
                <motion.div 
                  className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center lg:order-2 shadow-lg"
                  whileHover={{ 
                    scale: 1.1,
                    boxShadow: "0 25px 50px -12px rgba(59, 130, 246, 0.3)"
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Users className="h-8 w-8 text-white" />
                </motion.div>
              </motion.div>

              {/* Step 4 */}
              <motion.div 
                variants={item}
                className="flex items-center gap-6 lg:justify-start"
                whileHover={{ x: 10, scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div 
                  className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full flex items-center justify-center shadow-lg"
                  whileHover={{ 
                    scale: 1.1,
                    boxShadow: "0 25px 50px -12px rgba(59, 130, 246, 0.3)"
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Shield className="h-8 w-8 text-white" />
                </motion.div>
                <div className="text-left">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-blue-700 font-semibold">احجز العقار</span>
                    <h3 className="text-xl font-bold text-blue-900">الخطوة 4</h3>
                  </div>
                  <p className="text-blue-800">احجز عقارك المفضل بخطوات سهلة</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* Features Section */}
        <section className="container mx-auto px-4 py-16">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-blue-900 mb-4">لماذا تختار كنف؟</h2>
            <p className="text-blue-800 text-lg">نوفر لك أفضل تجربة في البحث عن العقارات</p>
          </motion.div>
          
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={container}
          >
            {features.map((feature, index) => (
               <motion.div 
                 key={index} 
                 variants={item}
                 whileHover={{ 
                   y: -10,
                   scale: 1.02,
                   boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.1)"
                 }}
                 transition={{ duration: 0.3 }}
               >
                 <Card className="text-center h-full border border-blue-100 hover:border-blue-300 transition-all bg-white shadow-sm hover:shadow-md">
                   <CardHeader>
                     <motion.div 
                       className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-full flex items-center justify-center mb-4 group"
                       whileHover={{ 
                         scale: 1.1,
                         background: "linear-gradient(135deg, rgb(219, 234, 254), rgb(207, 250, 254))"
                       }}
                       whileTap={{ scale: 0.95 }}
                     >
                       <feature.icon className="h-8 w-8 text-blue-600 group-hover:text-blue-700 transition-colors" />
                     </motion.div>
                     <CardTitle className="text-xl text-blue-900">{feature.title}</CardTitle>
                   </CardHeader>
                   <CardContent>
                     <CardDescription className="text-blue-800">
                       {feature.description}
                     </CardDescription>
                   </CardContent>
                 </Card>
               </motion.div>
            ))}
          </motion.div>
        </section>

        {/* CTA Section */}
        <motion.section 
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="bg-gradient-to-r from-blue-700 to-cyan-600 text-white py-16 relative overflow-hidden"
        >
          <motion.div 
            className="absolute top-0 left-0 w-full h-full opacity-10"
            animate={{ 
              backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
            }}
            transition={{ 
              duration: 15, 
              repeat: Infinity 
            }}
            style={{
              backgroundImage: 'radial-gradient(circle, white 2px, transparent 2px)',
              backgroundSize: '40px 40px'
            }}
          />
          
          <div className="container mx-auto px-4 text-center relative z-10">
            <motion.h2 
              className="text-3xl font-bold mb-4"
              initial={{ scale: 0.9 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring" }}
            >
              هل أنت مالك عقار؟
            </motion.h2>
            
            <motion.p 
              className="text-xl mb-8 opacity-90"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 0.9 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              انضم إلينا وابدأ في تأجير عقارك اليوم
            </motion.p>
            
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button 
                asChild 
                size="lg" 
                className="text-lg px-8 py-3 bg-white text-blue-700 hover:bg-blue-50 shadow-lg hover:shadow-xl transition-all"
              >
                <Link to="/register" className="flex items-center">
                  ابدأ كمالك عقار
                  <MoveRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </motion.section>
      </div>

      <Footer />
    </motion.div></>
  );
};

export default Index;